/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Hidden, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { GraduationCapIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/degree-home';

import type { ProgressBarPropertiesType, ProgressBarPropertyNameType } from '.';

type LegendProps = { legendItems: ProgressBarPropertiesType[]; isDegreeComplete?: boolean };

type LegendItemProps = {
  legendKey: ProgressBarPropertyNameType;
  showSeparator?: boolean;
};

export const progressBarColorMap = {
  completed: 'var(--cds-color-feedback-success)',
  inProgress: 'var(--cds-color-callouts-tertiary)',
  remaining: 'var(--cds-color-neutral-disabled-strong)',
  planned: 'var(--cds-color-utility-warning)',
};

export const METADATA_SEPARATOR = ' · ';

const styles = {
  container: css`
    display: flex;
    white-space: nowrap;
  `,
  legend: css`
    display: flex;
    align-items: flex-start;
    gap: var(--cds-spacing-50);
    align-self: stretch;
    box-sizing: unset;
    padding: var(--cds-spacing-50);
    border: 1px solid var(--cds-color-neutral-disabled);
    border-radius: 6px;
    height: var(--cds-spacing-200);
    width: var(--cds-spacing-100);
    margin-right: var(--cds-spacing-100);
  `,
  barFiller: (legendKey: ProgressBarPropertyNameType) => css`
    border-radius: 2px;
    height: 100%;
    width: 100%;
    background: ${progressBarColorMap[legendKey]};
  `,
  separator: css`
    ${typography2.subtitleMedium}
    color: var(--cds-color-neutral-stroke-primary);
    margin: 0 var(--cds-spacing-100);
  `,
  legendContainer: css`
    display: flex;
    align-items: center;
    ${breakpoints.down('xs')} {
      flex-direction: column;
      gap: var(--cds-spacing-150);
      align-items: flex-start;
    }

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      row-gap: var(--cds-spacing-150);
    }
  `,
  congratulationsSection: css`
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: center;
  `,
  graduationCap: css`
    color: var(--cds-color-interactive-primary-pressed);
  `,
};

const MetadataSeparator: React.VFC = () => (
  <Hidden xsDown>
    <div aria-hidden="true" data-testid="metadata-separator" css={styles.separator}>
      {METADATA_SEPARATOR}
    </div>
  </Hidden>
);

const LegendColorBlock: React.FC<{ legendKey: ProgressBarPropertyNameType }> = ({ legendKey }) => {
  return (
    <div aria-hidden="true" css={styles.legend}>
      <div css={styles.barFiller(legendKey)} />
    </div>
  );
};

export const LegendItem: React.FC<LegendItemProps> = ({ legendKey, showSeparator = false, children }) => {
  const legendAriaLabel = (): string => {
    switch (legendKey) {
      case 'completed':
        return _t('Completed credits legend item');
      case 'inProgress':
        return _t('In progress credits legend item');
      case 'planned':
        return _t('Planned credits legend item');
      case 'remaining':
        return _t('Remaining credits legend item');
      default:
        return '';
    }
  };

  return (
    <React.Fragment>
      <div aria-label={legendAriaLabel()} css={styles.container}>
        <LegendColorBlock legendKey={legendKey} />
        <Typography2 component="div" id={`${legendKey}-bar`}>
          {children}
        </Typography2>
      </div>
      {showSeparator && <MetadataSeparator />}
    </React.Fragment>
  );
};

const LegendLabel: React.FC<{ legendItem: ProgressBarPropertiesType; isFirstLegendItem: boolean }> = ({
  legendItem,
  isFirstLegendItem,
}) => {
  switch (legendItem.name) {
    case 'completed':
      return (
        <FormattedMessage
          message={
            isFirstLegendItem
              ? _t('{completed, number} {completed, plural, =1 {credit} other {credits}} earned')
              : _t('{completed, number} completed')
          }
          completed={legendItem.credit}
        />
      );
    case 'inProgress':
      return (
        <FormattedMessage
          message={
            isFirstLegendItem
              ? _t('{completed, number} {completed, plural, =1 {credit} other {credits}} in progress')
              : _t('{completed, number} in progress')
          }
          completed={legendItem.credit}
        />
      );
    case 'planned':
      return (
        <FormattedMessage
          message={
            isFirstLegendItem
              ? _t('{completed, number} {completed, plural, =1 {credit} other {credits}} added to plan')
              : _t('{completed, number} added to plan')
          }
          completed={legendItem.credit}
        />
      );
    case 'remaining':
      return (
        <FormattedMessage
          message={
            isFirstLegendItem
              ? _t('{completed, number} {completed, plural, =1 {credit} other {credits}} remaining')
              : _t('{completed, number} remaining')
          }
          completed={legendItem.credit}
        />
      );
    default:
      return null;
  }
};

const CongratulationsBanner: React.VFC = () => (
  <React.Fragment>
    <MetadataSeparator />

    <div aria-label={_t('Degree completed banner')} css={styles.congratulationsSection}>
      <GraduationCapIcon css={styles.graduationCap} />
      <Typography2 variant="subtitleMedium" color="highlightPurple" component="span">
        {_t('Congratulations!')}
      </Typography2>
    </div>
  </React.Fragment>
);

const Legend: React.FC<LegendProps> = ({ legendItems, isDegreeComplete = false }) => {
  return (
    <div css={styles.legendContainer} aria-label={_t('Degree progress legend')}>
      {legendItems.map((legendItem, index) => {
        return (
          <LegendItem key={legendItem.name} legendKey={legendItem.name} showSeparator={index < legendItems.length - 1}>
            <LegendLabel legendItem={legendItem} isFirstLegendItem={index === 0} />
          </LegendItem>
        );
      })}

      {isDegreeComplete && <CongratulationsBanner />}
    </div>
  );
};

export default Legend;
